import api from './api';

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  markdown_body: string;
  variables: string[];
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserEmailTemplateOverride {
  id: number;
  user_id: number;
  email_template_id: number;
  subject?: string;
  body?: string;
  markdown_body?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  email_template?: EmailTemplate;
}

export interface CreateEmailTemplateData {
  name: string;
  subject: string;
  body: string;
  markdown_body?: string;
  variables?: string[];
  description?: string;
  is_active?: boolean;
}

export interface UpdateEmailTemplateData extends Partial<CreateEmailTemplateData> {
  id: number;
}

export interface CreateUserOverrideData {
  subject?: string;
  body?: string;
  markdown_body?: string;
  is_active?: boolean;
}

export interface EmailTemplatePreview {
  template: EmailTemplate;
  rendered: {
    subject: string;
    body: string;
    markdown_body: string;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

class EmailTemplateService {
  // Get all email templates
  async getTemplates(page = 1, perPage = 15): Promise<ApiResponse<PaginatedResponse<EmailTemplate>>> {
    const response = await api.get(`/email-templates?page=${page}&per_page=${perPage}`);
    return response.data;
  }

  // Get specific email template
  async getTemplate(id: number): Promise<ApiResponse<EmailTemplate>> {
    const response = await api.get(`/email-templates/${id}`);
    return response.data;
  }

  // Create new email template
  async createTemplate(data: CreateEmailTemplateData): Promise<ApiResponse<EmailTemplate>> {
    const response = await api.post('/email-templates', data);
    return response.data;
  }

  // Update email template
  async updateTemplate(id: number, data: Partial<CreateEmailTemplateData>): Promise<ApiResponse<EmailTemplate>> {
    const response = await api.put(`/email-templates/${id}`, data);
    return response.data;
  }

  // Delete email template
  async deleteTemplate(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete(`/email-templates/${id}`);
    return response.data;
  }

  // Preview email template with sample data
  async previewTemplate(id: number, variables: Record<string, any> = {}): Promise<ApiResponse<EmailTemplatePreview>> {
    const response = await api.post(`/email-templates/${id}/preview`, { variables });
    return response.data;
  }

  // Get user's template overrides
  async getUserOverrides(): Promise<ApiResponse<UserEmailTemplateOverride[]>> {
    const response = await api.get('/email-templates/user/overrides');
    return response.data;
  }

  // Create or update user template override
  async createOrUpdateUserOverride(templateId: number, data: CreateUserOverrideData): Promise<ApiResponse<UserEmailTemplateOverride>> {
    const response = await api.post(`/email-templates/${templateId}/user-override`, data);
    return response.data;
  }

  // Delete user template override
  async deleteUserOverride(templateId: number): Promise<ApiResponse<void>> {
    const response = await api.delete(`/email-templates/${templateId}/user-override`);
    return response.data;
  }

  // Generate sample data for template variables
  generateSampleData(variables: string[]): Record<string, any> {
    const sampleFirm = {
      id: 1,
      name: 'Sample Law Firm',
      phone: '(908) 268-0592',
      website: 'http://pvexperts.com',
    };

    const sampleCase = {
      id: 1,
      case_name: 'Sample Case',
      case_type: 'Plaintiff',
      case_state: 'NJ',
      due_date: new Date().toISOString(),
      status: 'Approved',
      firm: sampleFirm,
      serviceType: {
        id: 1,
        name: 'Life Care Plan',
        short_code: 'LCP',
        price: 5000.0,
      },
    };

    const sampleData: Record<string, any> = {};
    
    variables.forEach(variable => {
      switch (variable) {
        case 'userName':
          sampleData.userName = 'John Doe';
          break;
        case 'case':
          sampleData.case = sampleCase;
          break;
        case 'serviceType':
          sampleData.serviceType = sampleCase.serviceType;
          break;
        case 'firm':
          sampleData.firm = sampleFirm;
          break;
        case 'firmName':
          sampleData.firmName = sampleFirm.name;
          break;
        case 'firmPhone':
          sampleData.firmPhone = sampleFirm.phone;
          break;
        case 'firmWebsite':
          sampleData.firmWebsite = sampleFirm.website;
          break;
        case 'caseName':
          sampleData.caseName = sampleCase.case_name;
          break;
        case 'caseType':
          sampleData.caseType = sampleCase.case_type;
          break;
        case 'serviceTypeName':
          sampleData.serviceTypeName = sampleCase.serviceType.name;
          break;
        case 'caseStatus':
          sampleData.caseStatus = sampleCase.status;
          break;
        case 'expert':
          sampleData.expert = {
            id: 1,
            name: 'Jane Expert',
            email: 'expert@example.com',
          };
          break;
        case 'expertName':
          sampleData.expertName = 'Jane Expert';
          break;
        case 'intakeForm':
          sampleData.intakeForm = {
            id: 1,
            intakeFormType: { name: 'Standard Intake Form' },
            submitter_signature_date: new Date().toISOString(),
          };
          break;
        case 'intakeFormLinks':
        case 'links':
          sampleData.intakeFormLinks = [
            {
              is_required: true,
              public_url: 'https://intake.pvexperts.com/intake-form/sample-token',
              intake_form_type: {
                name: 'Standard Intake Form',
                description: 'Please complete this intake form.',
              },
            },
          ];
          if (variable === 'links') {
            sampleData.links = sampleData.intakeFormLinks;
          }
          break;
        case 'meetingLocation':
          sampleData.meetingLocation = '54 Old Hwy 22, Clinton, NJ 08809';
          break;
        case 'meetingDetails':
          sampleData.meetingDetails = {
            meeting_type: 'virtual',
            meeting_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            meeting_link: 'https://teams.microsoft.com/l/meetup-join/sample',
            meeting_location: '54 Old Hwy 22, Clinton, NJ 08809',
          };
          break;
        case 'meeting':
          sampleData.meeting = {
            start_time: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
            end_time: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000).toISOString(),
          };
          break;
        case 'availableSlots':
          sampleData.availableSlots = [sampleData.meeting || {
            start_time: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
            end_time: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000 + 60 * 60 * 1000).toISOString(),
          }];
          break;
        case 'registrationData':
          sampleData.registrationData = {
            firm_name: sampleFirm.name,
            contact_name: 'John Doe',
            email: 'john@example.com',
          };
          break;
        case 'isRush':
          sampleData.isRush = true;
          break;
        case 'rushFeeAmount':
          sampleData.rushFeeAmount = 1000.00;
          break;
        case 'attachments':
          sampleData.attachments = ['file1.pdf', 'file2.pdf'];
          break;
        case 'uploadData':
          sampleData.uploadData = {
            upload_date: new Date().toISOString(),
            file_count: 2,
          };
          break;
        case 'isSystemAdmin':
          sampleData.isSystemAdmin = false;
          break;
        case 'user':
          sampleData.user = {
            id: 1,
            name: 'John Doe',
            email: 'john@example.com',
            firm_id: 1,
            provider: null,
            created_at: new Date().toISOString(),
            firm: sampleFirm,
          };
          break;
        case 'resetUrl':
          sampleData.resetUrl = 'https://intake.pvexperts.com/reset-password?token=abc123';
          break;
        case 'loginUrl':
          sampleData.loginUrl = 'https://intake.pvexperts.com/login';
          break;
        case 'temporaryPassword':
          sampleData.temporaryPassword = 'TempPass123!';
          break;
        case 'rejectionReason':
          sampleData.rejectionReason = 'Sample rejection reason';
          break;
        case 'paymentDetails':
          sampleData.paymentDetails = {
            retainer_amount: 2500.0,
            balance_amount: 2500.0,
          };
          break;
        case 'assignedBy':
          sampleData.assignedBy = 'Admin User';
          break;
        default:
          sampleData[variable] = `Sample ${variable}`;
          break;
      }
    });
    
    return sampleData;
  }
}

export default new EmailTemplateService();
